/* eslint-disable no-console */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { InputProps } from "@material-ui/core";
import { FC, useEffect, useRef, useState } from "react";
import NumberFormat, { NumberFormatValues } from "react-number-format";
import useDecimalSeparator from "src/hooks/personalization/useDecimalSeparator";
import { noop } from "../../../../globalConsts";
import { applyRef } from "../../../../utils/ref";
import { textFieldCss } from "../commonStyles";
import { NumberInputFormatConfig } from "./inputFormat";
import { getValueObjectFromNumberFormatComponentInstance } from "./utils";

const INCREMENT_STEP = 1;

type Props = {
  inputRef: NonNullable<InputProps["inputRef"]>;
  // TextField injectuje svoji `onChange` funkci, která musí brát event
  // Přijde mi čistší si sem poslat svůj alternativní onChange na jinak pojmenovaném
  // propu, než mockovat event, jak to dělají v docs:
  // https://codesandbox.io/s/ope4o?file=/demo.tsx - řádek 54
  customOnChange: (newVal: number | null) => void;
  onBlur(): void;
  formatConfig: NumberInputFormatConfig;
  required: boolean;
  value: number | null | string;
  // Plus co dostane od Mui TextField
};

const CustomInputComponent: FC<Props> = (props) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ref = useRef<NumberFormat<any>>(null);
  const localInputRef = useRef<HTMLInputElement>(null); // Treba druhou ref instanci, bo inputRef muze byt callback
  const decimalSeparator = useDecimalSeparator();

  const {
    required,
    inputRef,
    customOnChange = noop,
    onBlur,
    formatConfig,
    value,
    ...otherProps
  } = props;
  const { minValue, maxValue, ...otherConfig } = formatConfig;

  const handleChange = ({ floatValue }: NumberFormatValues): void => {
    customOnChange(floatValue ?? null);
  };

  const isAllowed = ({ floatValue }: NumberFormatValues): boolean => {
    if (floatValue === undefined) return true;

    if (minValue && floatValue < minValue) return false;
    if (maxValue && floatValue > maxValue) return false;

    return true;
  };

  const incrementValue = ({ floatValue = 0 }: NumberFormatValues): void => {
    customOnChange(floatValue + INCREMENT_STEP);
  };

  const decrementValue = ({ floatValue = 0 }: NumberFormatValues): void => {
    customOnChange(floatValue - INCREMENT_STEP);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!ref.current) return;
    const values = getValueObjectFromNumberFormatComponentInstance(ref.current);

    if (e.key === "ArrowUp") incrementValue(values);
    if (e.key === "ArrowDown") decrementValue(values);
  };

  const onClick = () => localInputRef.current?.select();

  const hasError = required && !value && value !== 0;

  return (
    <NumberFormat
      {...otherProps}
      {...otherConfig}
      onFocus={onClick}
      decimalSeparator={decimalSeparator}
      required={required}
      isNumericString
      css={textFieldCss(hasError)}
      onKeyDown={onKeyDown}
      getInputRef={(el: HTMLInputElement) => {
        applyRef(localInputRef, el);
        applyRef(inputRef, el);
      }}
      onValueChange={handleChange}
      onBlur={onBlur}
      isAllowed={isAllowed}
      ref={ref}
      value={value}
    />
  );
};

export type CustomInputComponentProps = Props;
export default CustomInputComponent;
